import React from 'react';
import {
  AUTHOR_ROLE_PRESENTATIONS,
  REPLY_DESIGNATION_PRESENTATIONS,
  resolveAuthorRolePresentation,
  resolveEditorialCorrectionLabel,
  resolveReplyDesignationPresentation,
  type PublicReply,
} from '@find-my-part/types';
import { formatTimeAgo } from '../../lib/formatTimeAgo';
import { ImageThumbnailGrid } from '../ImageThumbnailGrid/ImageThumbnailGrid';

export interface ReplyItemProps {
  reply: PublicReply;
}

export function ReplyItem({ reply }: ReplyItemProps): React.ReactElement {
  const authorPresentation = resolveAuthorRolePresentation(reply.userType);
  const replyPresentation = reply.isSolution
    ? REPLY_DESIGNATION_PRESENTATIONS.SOLUTION
    : resolveReplyDesignationPresentation(reply.replyType);
  const showStaffBadge = authorPresentation === AUTHOR_ROLE_PRESENTATIONS.ADMIN;

  return (
    <li className={`fmp-reply ${reply.isSolution ? 'fmp-reply--solution' : ''}`}>
      {reply.isSolution && (
        <div className="fmp-reply__solution-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true" className="fmp-reply__solution-icon">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {replyPresentation.customerLabel}
        </div>
      )}
      <div className="fmp-reply__body">{reply.body}</div>
      {/* Approved reply images (PR6): a smaller, contextual strip under the reply
          body — never a second primary gallery, and never inflates the reply
          count. Renders nothing when the reply has no serveable images. */}
      <ImageThumbnailGrid
        images={reply.images}
        labelNoun="Reply image"
        groupLabel="Reply images"
        variant="compact"
      />
      <div className="fmp-reply__meta">
        <span className="fmp-reply__author">
          {reply.authorName}
          {showStaffBadge && (
            <span className="fmp-reply__admin-badge">{authorPresentation.customerLabel}</span>
          )}
        </span>
        <span className="fmp-reply__separator" aria-hidden="true">&middot;</span>
        <time dateTime={new Date(reply.createdAt).toISOString()}>
          {formatTimeAgo(reply.createdAt)}
        </time>
        {reply.editoriallyCorrected && (
          <span className="fmp-editorial-marker">
            {resolveEditorialCorrectionLabel(reply.userType)}
          </span>
        )}
      </div>
    </li>
  );
}
